import { FetchResult } from 'apollo-link';
import { GraphQLError } from 'graphql';
import moment from 'moment';

import { extractUpdatedGroupIds } from '../service/ErrorHelpers/ErrorHelpers';
import {
    fetchInterfaceLangs,
    getStringDictByTargetingKey,
    INTERNAL_GROUP_SUGGEST_FIELDS,
    INTERNAL_GROUP_SUGGEST_WITH_VERSION_FIELDS,
    INTERNAL_GROUP_TEXT_FIELDS
} from '../service/InternalGroupHelpers/InternalGroupHelpers';
import {
    EQUALS_OPERATOR,
    extendTagValue,
    getComparativeOperatorsDictionary,
    getDefaultTagCloudTargetingState,
    getTagCloudTargetingId,
    GREATER_THAN_OR_EQUAL_TO_OPERATOR,
    isTagIncluded,
    LESS_THAN_OR_EQUAL_TO_OPERATOR,
    mapTagValueToString
} from '../service/TagCloudTargetingHelper/TagCloudTargetingHelper';
import { DictionaryItem } from '../typings/common';
import { DatesIntervalEditorsState } from '../typings/datesInterval';
import {
    addInternalAdGroups,
    GdAdditionalTargetingJoinType,
    GdAdditionalTargetingMode,
    GdAdditionalTargetingUatraitsInput,
    GdAdditionalTargetingVersionedInput,
    updateInternalAdGroups
} from '../typings/graphql-grid';
import { EditInternalGroupData, EditInternalGroupDataValue } from '../typings/groupsEditState';
import {
    AdditionalTargetingField,
    DeviceOptionsRecord,
    InputExternalValue,
    InputExternalValueKey,
    InternalGroupsSaveResponse,
    RadioTargetingValue,
    SuggestTargetingKey,
    SuggestTargetingValue,
    SuggestWithStringDictTargetingKey,
    SuggestWithStringDictTargetingValue,
    SuggestWithVersionTargetingKey,
    SuggestWithVersionTargetingValue
} from '../typings/internalGroups';
import { StringDicts } from '../typings/state';
import {
    TagCloudTargeting,
    TagCloudTargetingsState,
    TagValue,
    TargetingTagType,
    TargetingTagValue
} from '../typings/tagCloudTargeting';

export const VALUE_LENGTH_IF_FIRST_EDIT_STAGE_INCOMPLETE = 0;
export const VALUE_LENGTH_IF_SECOND_EDIT_STAGE_INCOMPLETE = 2;
export const VALUE_LENGTH_IF_THIRD_EDIT_STAGE_INCOMPLETE = 4;

export const mapTagCloudTargetingTextValue = (editor: TagCloudTargeting): InputExternalValue | undefined => {
    const { ALL, ANY } = GdAdditionalTargetingJoinType;
    const { FILTERING, TARGETING } = GdAdditionalTargetingMode;
    const { tags } = editor;

    if (tags.length === 0) {
        return undefined;
    }

    const targetingMode = tags.some(isTagIncluded) ? TARGETING : FILTERING;
    const joinType = targetingMode === TARGETING ? ANY : ALL;

    // FIXME: https://st.yandex-team.ru/DIRECT-104236
    //        Когда будет новое API, поддержать его здесь
    const value = tags.map(tag => mapTagValueToString(tag.value));

    return {
        targetingMode,
        joinType,
        value
    };
};

const mapEditorValueToVersionedInput = (
    tags: TargetingTagValue[],
    dictionaryItems: DictionaryItem[]
): GdAdditionalTargetingVersionedInput[] => {
    return tags.reduce(
        (acc: GdAdditionalTargetingVersionedInput[], tag: TargetingTagValue): GdAdditionalTargetingVersionedInput[] => {
            const { value } = tag;
            if (typeof value === 'string') {
                return [];
            }

            if (
                value.length === VALUE_LENGTH_IF_FIRST_EDIT_STAGE_INCOMPLETE ||
                value.length === VALUE_LENGTH_IF_SECOND_EDIT_STAGE_INCOMPLETE ||
                value.length === VALUE_LENGTH_IF_THIRD_EDIT_STAGE_INCOMPLETE
            ) {
                return acc;
            }

            const selectedItem = dictionaryItems.find(item => item.text === value[0]);

            if (!selectedItem) {
                return [...acc, { targetingValueEntryId: '-1' }];
            }

            const targetingValue: GdAdditionalTargetingVersionedInput = {
                targetingValueEntryId: String(selectedItem.val)
            };
            const equalsOperatorIndex = value.indexOf(EQUALS_OPERATOR);

            let minVersionIndex = -1;
            let maxVersionIndex = -1;

            if (equalsOperatorIndex !== -1) {
                minVersionIndex = equalsOperatorIndex;
                maxVersionIndex = equalsOperatorIndex;
            } else {
                minVersionIndex = value.indexOf(GREATER_THAN_OR_EQUAL_TO_OPERATOR);
                maxVersionIndex = value.indexOf(LESS_THAN_OR_EQUAL_TO_OPERATOR);
            }

            if (minVersionIndex !== -1) {
                targetingValue.minVersion = value[minVersionIndex + 1];
            }

            if (maxVersionIndex !== -1) {
                targetingValue.maxVersion = value[maxVersionIndex + 1];
            }

            return [...acc, targetingValue];
        },
        []
    );
};

const mapEditorValueToUaTraitsInput = (
    tags: TargetingTagValue[],
    dictionaryItems: DictionaryItem[]
): GdAdditionalTargetingUatraitsInput[] => {
    return tags.reduce(
        (acc: GdAdditionalTargetingUatraitsInput[], tag: TargetingTagValue): GdAdditionalTargetingUatraitsInput[] => {
            const { value } = tag;
            if (typeof value === 'string') {
                return [];
            }

            const selectedItem = dictionaryItems.find(item => item.text === value[0]);

            if (!selectedItem) {
                return [...acc, { targetingValueEntryId: '-1' }];
            }

            return [...acc, { targetingValueEntryId: String(selectedItem.val) }];
        },
        []
    );
};

const mapEditorValueToStringArray = (tags: TargetingTagValue[], dictionaryItems: DictionaryItem[]): string[] => {
    return tags.reduce((acc: string[], tag: TargetingTagValue): string[] => {
        const { value } = tag;
        if (typeof value === 'string') {
            return [];
        }

        const selectedItem = dictionaryItems.find(item => item.text === value[0]);

        if (!selectedItem) {
            return [...acc, '-1'];
        }

        return [...acc, String(selectedItem.val)];
    }, []);
};

export const mapTagCloudTargetingSuggestValue = (
    editor: TagCloudTargeting,
    dictionaryItems: DictionaryItem[] = []
): SuggestTargetingValue | undefined => {
    const { ALL, ANY } = GdAdditionalTargetingJoinType;
    const { FILTERING, TARGETING } = GdAdditionalTargetingMode;
    const { tags } = editor;

    if (tags.length === 0) {
        return undefined;
    }

    const targetingMode = tags.some(isTagIncluded) ? TARGETING : FILTERING;
    const joinType = targetingMode === TARGETING ? ANY : ALL;

    return {
        targetingMode,
        joinType,
        value: mapEditorValueToStringArray(tags, dictionaryItems)
    };
};

export const mapTagCloudTargetingSuggestStringDictValue = (
    editor: TagCloudTargeting,
    dictionaryItems: DictionaryItem[] = []
): SuggestWithStringDictTargetingValue | undefined => {
    const { ALL, ANY } = GdAdditionalTargetingJoinType;
    const { FILTERING, TARGETING } = GdAdditionalTargetingMode;
    const { tags } = editor;

    if (tags.length === 0) {
        return undefined;
    }

    const targetingMode = tags.some(isTagIncluded) ? TARGETING : FILTERING;
    const joinType = targetingMode === TARGETING ? ANY : ALL;

    return {
        targetingMode,
        joinType,
        value: mapEditorValueToUaTraitsInput(tags, dictionaryItems)
    };
};

export const mapTagCloudTargetingSuggestWithVersionValue = (
    editor: TagCloudTargeting,
    dictionaryItems: DictionaryItem[] = []
): SuggestWithVersionTargetingValue | undefined => {
    const { ALL, ANY } = GdAdditionalTargetingJoinType;
    const { FILTERING, TARGETING } = GdAdditionalTargetingMode;
    const { tags } = editor;

    if (tags.length === 0) {
        return undefined;
    }

    const targetingMode = tags.some(isTagIncluded) ? TARGETING : FILTERING;
    const joinType = targetingMode === TARGETING ? ANY : ALL;

    return {
        targetingMode,
        joinType,
        value: mapEditorValueToVersionedInput(tags, dictionaryItems)
    };
};

export const mapTagCloudTargetingToDeviceOptionsRecord = (
    tagCloudTargeting: TagCloudTargeting
): DeviceOptionsRecord => {
    const { ALL, ANY } = GdAdditionalTargetingJoinType;
    const { FILTERING, TARGETING } = GdAdditionalTargetingMode;
    const { tags } = tagCloudTargeting;

    return tags.reduce((acc, tag) => {
        const targetingMode = isTagIncluded(tag) ? TARGETING : FILTERING;
        const joinType = targetingMode === TARGETING ? ANY : ALL;

        return {
            ...acc,
            [mapTagValueToString(tag.value)]: {
                targetingMode,
                joinType
            }
        };
    }, {});
};

export const mapTagCloudRadioTargetingEditorData = (editor: TagCloudTargeting): RadioTargetingValue | undefined => {
    const { ALL, ANY } = GdAdditionalTargetingJoinType;
    const { FILTERING, TARGETING } = GdAdditionalTargetingMode;
    const { tags } = editor;

    if (tags.length === 0) {
        return undefined;
    }

    const targetingMode = tags.some(isTagIncluded) ? TARGETING : FILTERING;
    const joinType = targetingMode === TARGETING ? ANY : ALL;

    return {
        targetingMode,
        joinType
    };
};

export const mapDatesIntervalEditorsDataFromInternalGroups = (
    groupRecord: Record<string, EditInternalGroupData>
): DatesIntervalEditorsState | undefined => {
    const groupIds = Object.keys(groupRecord);

    if (groupIds.length === 0) {
        return;
    }

    return groupIds.reduce((acc, groupId) => {
        const currentGroupValue = groupRecord[groupId].value;
        const { startTime: startTimestamp, finishTime: finishTimestamp } = currentGroupValue;
        const defaultDate = moment().startOf('day');
        const startDate = startTimestamp ? moment(startTimestamp) : defaultDate;
        const endDate = finishTimestamp ? moment(finishTimestamp) : undefined;
        const startTime = startTimestamp && moment(startTimestamp).format('HH:mm');
        const endTime = finishTimestamp && moment(finishTimestamp).format('HH:mm');

        return {
            ...acc,
            [groupId]: {
                startDate,
                endDate,
                startTime,
                endTime
            }
        };
    }, {});
};

/**
 * Преобразует поля из значения группы в state редактора облака тегов
 *
 * Сюда попадают все таргетинги, у которых значение - массив строк или чисел
 * и которым не требуются подсказки при вводе
 *
 * @param groupData
 */
const mapInternalGroupTextFieldsToTextTagCloudTargeting = (
    groupData: Record<string, EditInternalGroupData>
): TagCloudTargetingsState | undefined => {
    const { TARGETING } = GdAdditionalTargetingMode;
    const groupIds = Object.keys(groupData);

    if (groupIds.length === 0) {
        return;
    }

    return groupIds.reduce((acc, groupId): TagCloudTargetingsState => {
        const groupValue = groupData[groupId].value;

        return INTERNAL_GROUP_TEXT_FIELDS.reduce(
            (resultState, field: InputExternalValueKey): TagCloudTargetingsState => {
                const groupFieldValue: InputExternalValue | undefined = groupValue[field];
                const key = getTagCloudTargetingId(field, groupId);

                if (groupFieldValue === undefined) {
                    return {
                        ...resultState,
                        [key]: getDefaultTagCloudTargetingState()
                    };
                }

                const { targetingMode, value } = groupFieldValue;

                const savedValue = {
                    tags: value.map(item =>
                        extendTagValue(
                            [item],
                            targetingMode === TARGETING ? TargetingTagType.INCLUDED : TargetingTagType.EXCLUDED
                        )
                    )
                };

                return {
                    ...resultState,
                    [key]: {
                        isEditorOpen: false,
                        savedValue,
                        ...savedValue
                    }
                };
            },
            acc
        );
    }, {});
};

const mapUaTraitsInputToTagCloudTargeting = (
    groupValue: EditInternalGroupDataValue,
    field: AdditionalTargetingField.DEVICE_VENDORS | AdditionalTargetingField.OS_NAMES,
    stringDict: DictionaryItem[]
): TagCloudTargeting => {
    const { TARGETING } = GdAdditionalTargetingMode;
    const fieldValue = groupValue[field];
    if (!fieldValue) {
        return getDefaultTagCloudTargetingState();
    }

    const { targetingMode, value } = fieldValue;

    const mappedValue = value.reduce((acc, item) => {
        const dictionaryItem = stringDict.find(it => it.val === item.targetingValueEntryId.toString());

        if (!dictionaryItem) {
            return acc;
        }

        return [...acc, [dictionaryItem.text]];
    }, []);

    const savedValue = {
        tags: mappedValue.map(item =>
            extendTagValue(item, targetingMode === TARGETING ? TargetingTagType.INCLUDED : TargetingTagType.EXCLUDED)
        )
    };

    return {
        ...savedValue,
        isEditorOpen: false,
        savedValue
    };
};

const mapStringInputToTagCloudTargeting = (
    groupValue: EditInternalGroupDataValue,
    field: SuggestTargetingKey,
    stringDict: DictionaryItem[]
): TagCloudTargeting => {
    const { TARGETING } = GdAdditionalTargetingMode;
    const fieldValue = groupValue[field];
    if (!fieldValue) {
        return getDefaultTagCloudTargetingState();
    }

    const { targetingMode, value } = fieldValue;

    const mappedValue = (value as string[]).reduce((acc: TagValue[], item): TagValue[] => {
        const dictionaryItem = stringDict.find(it => it.val === item.toString());

        if (!dictionaryItem) {
            return acc;
        }

        return [...acc, [dictionaryItem.text]];
    }, []);

    const savedValue = {
        tags: mappedValue.map(item =>
            extendTagValue(item, targetingMode === TARGETING ? TargetingTagType.INCLUDED : TargetingTagType.EXCLUDED)
        )
    };

    return {
        ...savedValue,
        isEditorOpen: false,
        savedValue
    };
};

/**
 * Преобразует поля из значения группы в state редактора облака тегов
 *
 * Сюда попадают все таргетинги, у которых есть справочник с подсказками для
 * ввода значений
 *
 * @param groupData
 * @param stringDicts
 */
const mapInternalGroupValueToSuggestTagCloudTargeting = (
    groupData: Record<string, EditInternalGroupData>,
    stringDicts?: StringDicts
): TagCloudTargetingsState | undefined => {
    const { TARGETING } = GdAdditionalTargetingMode;
    const groupIds = Object.keys(groupData);

    if (groupIds.length === 0) {
        return;
    }

    return groupIds.reduce((acc, groupId): TagCloudTargetingsState => {
        const groupValue = groupData[groupId].value;

        const suggestEditors = INTERNAL_GROUP_SUGGEST_FIELDS.reduce(
            (resultState, field: SuggestTargetingKey | SuggestWithStringDictTargetingKey) => {
                const key = getTagCloudTargetingId(field, groupId);
                const stringDictName = getStringDictByTargetingKey(field);

                if (!stringDicts) {
                    return {
                        ...resultState,
                        [key]: getDefaultTagCloudTargetingState()
                    };
                }

                const currentStringDict: DictionaryItem[] = stringDictName
                    ? stringDicts[stringDictName] || []
                    : fetchInterfaceLangs();

                switch (field) {
                    case AdditionalTargetingField.OS_NAMES:
                    case AdditionalTargetingField.DEVICE_VENDORS:
                        return {
                            ...resultState,
                            [key]: mapUaTraitsInputToTagCloudTargeting(groupValue, field, currentStringDict)
                        };

                    case AdditionalTargetingField.DESKTOP_INSTALLED_APPS:
                    case AdditionalTargetingField.INTERFACE_LANGS:
                        return {
                            ...resultState,
                            [key]: mapStringInputToTagCloudTargeting(groupValue, field, currentStringDict)
                        };

                    default:
                        return resultState;
                }
            },
            {}
        );

        const suggestWithVersionEditors = INTERNAL_GROUP_SUGGEST_WITH_VERSION_FIELDS.reduce(
            (resultState, field: SuggestWithVersionTargetingKey) => {
                const fieldValue: SuggestWithVersionTargetingValue | undefined = groupValue[field];
                const key = getTagCloudTargetingId(field, groupId);
                const stringDictName = getStringDictByTargetingKey(field);

                if (fieldValue === undefined || !stringDicts) {
                    return {
                        ...resultState,
                        [key]: getDefaultTagCloudTargetingState()
                    };
                }

                const currentStringDict = stringDictName ? stringDicts[stringDictName] || [] : [];

                const { targetingMode, value } = fieldValue;
                const mappedValue = value.map(item => {
                    const selectedDictionaryItem = currentStringDict.find(
                        dictionaryItem => dictionaryItem.val === item.targetingValueEntryId.toString()
                    );

                    if (!selectedDictionaryItem) {
                        return [];
                    }

                    const resultValue = [selectedDictionaryItem.text];

                    if (!item.minVersion && !item.maxVersion) {
                        return resultValue;
                    }

                    const [equalsOperator, greaterOperator, lessOperator] = getComparativeOperatorsDictionary();

                    if (item.minVersion === item.maxVersion) {
                        return [...resultValue, equalsOperator.text, item.minVersion];
                    }

                    if (item.minVersion) {
                        resultValue.push(greaterOperator.text, item.minVersion);
                    }

                    if (item.maxVersion) {
                        resultValue.push(lessOperator.text, item.maxVersion);
                    }

                    return resultValue;
                });

                const savedValue = {
                    tags: mappedValue.map(item =>
                        extendTagValue(
                            item as string[],
                            targetingMode === TARGETING ? TargetingTagType.INCLUDED : TargetingTagType.EXCLUDED
                        )
                    )
                };

                return {
                    ...resultState,
                    [key]: {
                        ...savedValue,
                        isEditorOpen: false,
                        savedValue
                    }
                };
            },
            {}
        );

        return {
            ...acc,
            ...suggestEditors,
            ...suggestWithVersionEditors
        };
    }, {});
};

const mapInternalGroupValueToDeviceOptionsTagCloudTargeting = (
    groupData: Record<string, EditInternalGroupData>
): TagCloudTargetingsState | undefined => {
    const { TARGETING } = GdAdditionalTargetingMode;
    const groupIds = Object.keys(groupData);

    if (groupIds.length === 0) {
        return;
    }

    return groupIds.reduce((acc, groupId): TagCloudTargetingsState => {
        const groupValue = groupData[groupId].value;
        const deviceOptionKeys = [
            AdditionalTargetingField.IS_MOBILE,
            AdditionalTargetingField.IS_TABLET,
            AdditionalTargetingField.IS_TOUCH
        ];
        const key = getTagCloudTargetingId(AdditionalTargetingField.DEVICE_OPTIONS, groupId);

        return {
            ...acc,
            [key]: deviceOptionKeys.reduce((resultState: TagCloudTargeting, optionKey): TagCloudTargeting => {
                const fieldValue = groupValue[optionKey];
                if (!fieldValue) {
                    return resultState;
                }

                const savedValue = {
                    tags: [
                        ...resultState.tags,
                        extendTagValue(
                            optionKey,
                            fieldValue.targetingMode === TARGETING
                                ? TargetingTagType.INCLUDED
                                : TargetingTagType.EXCLUDED
                        )
                    ]
                };

                return {
                    ...resultState,
                    ...savedValue,
                    savedValue
                };
            }, getDefaultTagCloudTargetingState())
        };
    }, {});
};

const mapInternalGroupValueToToggleTagCloudTargeting = (
    groupData: Record<string, EditInternalGroupData>,
    field: AdditionalTargetingField
): TagCloudTargetingsState | undefined => {
    const { TARGETING } = GdAdditionalTargetingMode;
    const groupIds = Object.keys(groupData);

    if (groupIds.length === 0) {
        return;
    }

    return groupIds.reduce((acc, groupId): TagCloudTargetingsState => {
        const key = getTagCloudTargetingId(field, groupId);
        const groupValue = groupData[groupId].value;
        const fieldValue = groupValue[field];

        if (!fieldValue) {
            return {
                ...acc,
                [key]: getDefaultTagCloudTargetingState()
            };
        }

        const { targetingMode } = fieldValue;
        const savedValue = {
            tags: [
                extendTagValue(
                    field,
                    targetingMode === TARGETING ? TargetingTagType.INCLUDED : TargetingTagType.EXCLUDED
                )
            ]
        };

        return {
            ...acc,
            [key]: {
                ...savedValue,
                isOpen: false,
                savedValue
            }
        };
    }, {});
};

/**
 * Преобразует данные группы в state редакторов таргетингов
 * Точка входа при инициализации редакторов
 *
 * @param groupData
 * @param stringDicts
 */
export const mapInternalGroupValueToTagCloudTargetings = (
    groupData: Record<string, EditInternalGroupData>,
    stringDicts?: StringDicts
): TagCloudTargetingsState => {
    return {
        ...mapInternalGroupTextFieldsToTextTagCloudTargeting(groupData),
        ...mapInternalGroupValueToSuggestTagCloudTargeting(groupData, stringDicts),
        ...mapInternalGroupValueToDeviceOptionsTagCloudTargeting(groupData),
        ...mapInternalGroupValueToToggleTagCloudTargeting(groupData, AdditionalTargetingField.IS_YANDEX_PLUS),
        ...mapInternalGroupValueToToggleTagCloudTargeting(groupData, AdditionalTargetingField.IS_VIRUSED),
        ...mapInternalGroupValueToToggleTagCloudTargeting(groupData, AdditionalTargetingField.INTERNAL_NETWORK)
    };
};

export const mapInternalGroupsAddResponseFromApiFormat = (
    response: FetchResult<addInternalAdGroups>
): InternalGroupsSaveResponse => {
    if (!response.data) {
        throw new Error('internal server error');
    }

    const {
        data: {
            addInternalAdGroups: { addedAdGroupItems, validationResult }
        }
    } = response;
    const errors: GraphQLError[] | undefined = [...response.errors];

    return {
        errors,
        validations: validationResult
            ? validationResult.errors.map(error => ({
                  path: error.path,
                  code: error.code,
                  params: error.params
              }))
            : [],
        updatedGroupIds: addedAdGroupItems.filter(item => Boolean(item)).map(item => (item ? item.adGroupId : ''))
    };
};

export const mapInternalGroupsUpdateResponseFromApiFormat = (
    response: FetchResult<updateInternalAdGroups>
): InternalGroupsSaveResponse => {
    if (!response.data) {
        throw new Error('internal server error');
    }

    const {
        data: {
            updateInternalAdGroups: { validationResult }
        }
    } = response;
    const errors: GraphQLError[] | undefined = [...response.errors];

    const updatedGroupIds = extractUpdatedGroupIds(response.data.updateInternalAdGroups);

    const validations = validationResult
        ? validationResult.errors.map(error => ({
              path: error.path,
              code: error.code,
              params: error.params
          }))
        : [];

    return { errors, validations, updatedGroupIds };
};
